import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, OnChanges, HostListener, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProcessedFile, WatermarkScript, BucketService } from '../../../services/bucket.service';

export type ViewMode = 'split' | 'fullscreen';
export type LayoutDirection = 'vertical' | 'horizontal';

@Component({
  selector: 'app-admin-details',
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatMenuModule,
    MatDividerModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule
  ],
  templateUrl: './admin-details.component.html',
  styleUrl: './admin-details.component.css'
})
export class AdminDetailsComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() selectedFile: ProcessedFile | null = null;
  @Input() isFullscreenMode: boolean = false;
  @Input() showMains: boolean = true;

  @Output() closeDetails = new EventEmitter<void>();
  @Output() showMainsChange = new EventEmitter<boolean>();
  @Output() viewModeChange = new EventEmitter<ViewMode>();
  @Output() fileNavigated = new EventEmitter<'previous' | 'next'>();

  // View configuration
  viewMode: ViewMode = 'split';
  layoutDirection: LayoutDirection = 'horizontal';
  showMetadata: boolean = true;
  showComparison: boolean = true;

  // Loading state for images
  isLoadingImages: boolean = false;
  originalImageLoaded: boolean = false;
  watermarkRemovedImageLoaded: boolean = false;

  // Bucket file preview blob URL
  bucketPreviewBlobUrl: string | null = null;
  isBucketImageLoading: boolean = false;
  private lastLoadedFilePath: string | null = null; // Cache to prevent reloading same image

  // Zoom and pan properties
  zoomLevel: number = 1;
  minZoom: number = 0.25;
  maxZoom: number = 4;
  zoomStep: number = 0.25;
  panX: number = 0;
  panY: number = 0;
  isPanning: boolean = false;
  lastPanX: number = 0;
  lastPanY: number = 0;

  // Validation properties
  availableScripts: WatermarkScript[] = [];
  selectedScript: string = '';
  isReprocessing: boolean = false;
  isAcceptingResult: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private bucketService: BucketService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    // Load available scripts
    this.loadAvailableScripts();
  }

  ngOnDestroy(): void {
    this.cleanupBlobUrl();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(): void {
    // Reset loading state when a new file is selected or view changes
    this.resetImageLoadingStates();
    
    // Load blob URL for bucket file preview - only if file path changed
    if (this.isBucketFilePreview() && this.selectedFile?.path && this.selectedFile?.bucket_name) {
      // Only reload if the file path actually changed
      if (this.selectedFile.path !== this.lastLoadedFilePath) {
        console.log('📷 Loading new image:', this.selectedFile.path);
        this.loadBucketPreviewImage();
      } else {
        console.log('📷 Skipping reload - same image already loaded:', this.selectedFile.path);
      }
    } else {
      // Clean up old blob URL if not a bucket preview
      this.cleanupBlobUrl();
      this.lastLoadedFilePath = null;
    }
  }

  /**
   * Load the bucket file image as a blob URL for authenticated preview
   */
  private loadBucketPreviewImage(): void {
    if (!this.selectedFile?.path || !this.selectedFile?.bucket_name) {
      console.error('Missing path or bucket_name:', {
        path: this.selectedFile?.path,
        bucket_name: this.selectedFile?.bucket_name
      });
      return;
    }
    
    console.log('Loading bucket preview image:', {
      path: this.selectedFile.path,
      bucket_name: this.selectedFile.bucket_name
    });
    
    // Clean up previous blob URL
    this.cleanupBlobUrl();
    
    // Track the file path being loaded
    const loadingPath = this.selectedFile.path;
    
    this.isBucketImageLoading = true;
    
    this.bucketService.getImageBlobUrl(this.selectedFile.path, this.selectedFile.bucket_name)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blobUrl) => {
          this.bucketPreviewBlobUrl = blobUrl;
          this.lastLoadedFilePath = loadingPath; // Cache the loaded path
          this.isBucketImageLoading = false;
          this.changeDetectorRef.detectChanges();
        },
        error: (error) => {
          console.error('Failed to load bucket preview image:', error);
          this.isBucketImageLoading = false;
          this.lastLoadedFilePath = null; // Clear cache on error
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  /**
   * Clean up blob URL to avoid memory leaks
   */
  private cleanupBlobUrl(): void {
    if (this.bucketPreviewBlobUrl) {
      window.URL.revokeObjectURL(this.bucketPreviewBlobUrl);
      this.bucketPreviewBlobUrl = null;
    }
    // Note: Don't reset lastLoadedFilePath here - it's used for caching decisions
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (this.viewMode === 'fullscreen') {
      // Handle fullscreen keyboard shortcuts
      if (event.key === 'Escape') {
        event.preventDefault();
        this.exitFullscreen();
      } else if (event.key === 'F' || event.key === 'f') {
        event.preventDefault();
        this.toggleFullscreen();
      }

      // Zoom shortcuts
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        this.zoomIn();
      } else if (event.key === '-') {
        event.preventDefault();
        this.zoomOut();
      } else if (event.key === '0') {
        event.preventDefault();
        this.resetZoom();
      }

      // Navigation shortcuts
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.navigateFile('previous');
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.navigateFile('next');
      }
    }
  }

  onCloseDetails(): void {
    this.closeDetails.emit();
  }

  onShowMainsChange(): void {
    this.showMains = !this.showMains;
    this.showMainsChange.emit(this.showMains);
  }

  setViewMode(mode: ViewMode): void {
    this.viewMode = mode;
    this.viewModeChange.emit(mode);

    // Reset image loading states when changing view modes
    // This ensures loading indicators work correctly in different view modes
    this.resetImageLoadingStates();
  }

  toggleFullscreen(): void {
    if (this.viewMode === 'fullscreen') {
      this.setViewMode('split');
    } else {
      this.setViewMode('fullscreen');
    }
  }

  exitFullscreen(): void {
    this.setViewMode('split');
  }

  toggleLayoutDirection(): void {
    this.layoutDirection = this.layoutDirection === 'horizontal' ? 'vertical' : 'horizontal';
  }

  toggleMetadata(): void {
    this.showMetadata = !this.showMetadata;
  }

  toggleComparison(): void {
    this.showComparison = !this.showComparison;
  }

  getViewModeIcon(): string {
    return this.viewMode === 'fullscreen' ? 'fullscreen_exit' : 'fullscreen';
  }

  getLayoutIcon(): string {
    return this.layoutDirection === 'horizontal' ? 'view_column' : 'view_stream';
  }

  // Zoom Controls
  zoomIn(): void {
    if (this.canZoomIn()) {
      this.zoomLevel = Math.min(this.maxZoom, this.zoomLevel + this.zoomStep);
    }
  }

  zoomOut(): void {
    if (this.canZoomOut()) {
      this.zoomLevel = Math.max(this.minZoom, this.zoomLevel - this.zoomStep);
    }
  }

  resetZoom(): void {
    this.zoomLevel = 1;
    this.resetPan();
  }

  fitToScreen(): void {
    this.zoomLevel = 1;
    this.resetPan();
  }

  canZoomIn(): boolean {
    return this.zoomLevel < this.maxZoom;
  }

  canZoomOut(): boolean {
    return this.zoomLevel > this.minZoom;
  }

  getZoomPercentage(): string {
    return Math.round(this.zoomLevel * 100) + '%';
  }

  // Pan Controls
  resetPan(): void {
    this.panX = 0;
    this.panY = 0;
  }

  startPan(event: MouseEvent): void {
    if (this.zoomLevel > 1) {
      this.isPanning = true;
      this.lastPanX = event.clientX;
      this.lastPanY = event.clientY;
      event.preventDefault();
    }
  }

  onPan(event: MouseEvent): void {
    if (this.isPanning && this.zoomLevel > 1) {
      const deltaX = event.clientX - this.lastPanX;
      const deltaY = event.clientY - this.lastPanY;

      this.panX += deltaX;
      this.panY += deltaY;

      this.lastPanX = event.clientX;
      this.lastPanY = event.clientY;
    }
  }

  stopPan(): void {
    this.isPanning = false;
  }

  getImageTransform(): string {
    return `scale(${this.zoomLevel}) translate(${this.panX / this.zoomLevel}px, ${this.panY / this.zoomLevel}px)`;
  }

  // Navigation
  navigateFile(direction: 'previous' | 'next'): void {
    console.log('📍 AdminDetails: navigateFile called with direction:', direction);
    this.fileNavigated.emit(direction);
    console.log('📍 AdminDetails: fileNavigated event emitted');
  }

  canNavigatePrevious(): boolean {
    // This would typically check if there's a previous file in the list
    // For now, return true and let parent component handle the actual availability
    return true;
  }

  canNavigateNext(): boolean {
    // This would typically check if there's a next file in the list
    // For now, return true and let parent component handle the actual availability
    return true;
  }

  // Helper methods
  hasOriginalImage(): boolean {
    return !!(this.selectedFile?.original_page_url);
  }

  hasWatermarkRemovedImage(): boolean {
    return !!(this.selectedFile?.watermark_removed_url);
  }

  isBucketFilePreview(): boolean {
    return !!(this.selectedFile?.preview_url && !this.selectedFile?.watermark_removed_url);
  }

  getFileMetadata(): {[key: string]: any} {
    if (!this.selectedFile) return {};

    return {
      'Page ID': this.selectedFile.page_id,
      'Book Name': this.selectedFile.book_name,
      'Original URL': this.selectedFile.original_page_url ? 'Available' : 'Not Available',
      'Processed URL': this.selectedFile.watermark_removed_url ? 'Available' : 'Not Available',
      'Processing Status': this.hasWatermarkRemovedImage() ? 'Completed' : 'Pending'
    };
  }

  onImageError(event: any): void {
    console.log('Image error occurred:', event.target.getAttribute('data-image-type'));
    event.target.style.display = 'none';

    // Check if this was the original or watermark removed image using data attribute
    const imageType = event.target.getAttribute('data-image-type');
    if (imageType === 'original') {
      this.originalImageLoaded = true;
    } else if (imageType === 'watermark-removed') {
      this.watermarkRemovedImageLoaded = true;
    }
    this.checkAllImagesLoaded();
  }

  onImageLoad(event: any): void {
    const imageType = event.target.getAttribute('data-image-type');
    console.log('Image loaded successfully:', imageType);

    // Check if this was the original or watermark removed image using data attribute
    if (imageType === 'original') {
      this.originalImageLoaded = true;
    } else if (imageType === 'watermark-removed') {
      this.watermarkRemovedImageLoaded = true;
    }
    this.checkAllImagesLoaded();
  }

  private checkAllImagesLoaded(): void {
    // Check if all available images have loaded
    const hasOriginal = this.hasOriginalImage();
    const hasWatermarkRemoved = this.hasWatermarkRemovedImage();

    const originalReady = !hasOriginal || this.originalImageLoaded;
    const watermarkRemovedReady = !hasWatermarkRemoved || this.watermarkRemovedImageLoaded;

    console.log('Checking images loaded:', {
      hasOriginal,
      hasWatermarkRemoved,
      originalImageLoaded: this.originalImageLoaded,
      watermarkRemovedImageLoaded: this.watermarkRemovedImageLoaded,
      originalReady,
      watermarkRemovedReady,
      currentLoadingState: this.isLoadingImages
    });

    if (originalReady && watermarkRemovedReady) {
      this.isLoadingImages = false;
      console.log('All images loaded, hiding loading indicator');

      // Force change detection to ensure UI updates
      this.changeDetectorRef.detectChanges();
    }
  }

  // Helper method to reset image loading states
  private resetImageLoadingStates(): void {
    console.log('Resetting image loading states', {
      selectedFile: this.selectedFile?.page_id,
      viewMode: this.viewMode,
      hasOriginal: this.hasOriginalImage(),
      hasWatermarkRemoved: this.hasWatermarkRemovedImage()
    });

    if (this.selectedFile) {
      this.originalImageLoaded = false;
      this.watermarkRemovedImageLoaded = false;

      // Only show loading if there are images to load
      if (this.hasOriginalImage() || this.hasWatermarkRemovedImage()) {
        this.isLoadingImages = true;
        console.log('Starting image loading, showing loading indicator');

        // Force change detection to show loading indicator immediately
        this.changeDetectorRef.detectChanges();

        // Use setTimeout to handle cached images and ensure DOM updates
        setTimeout(() => {
          // Double-check that we still have images to load and same file
          if (this.selectedFile && (this.hasOriginalImage() || this.hasWatermarkRemovedImage())) {
            // Check if images might already be loaded from cache
            this.checkForCachedImages();
            this.checkAllImagesLoaded();
          }
        }, 50); // Reduced timeout for faster response

        // Failsafe: Hide loading indicator after maximum wait time
        setTimeout(() => {
          if (this.isLoadingImages) {
            console.log('Failsafe: Hiding loading indicator after timeout');
            this.isLoadingImages = false;
            this.changeDetectorRef.detectChanges();
          }
        }, 3000); // 3 second maximum loading time
      } else {
        this.isLoadingImages = false;
        console.log('No images to load, hiding loading indicator');
        this.changeDetectorRef.detectChanges();
      }
    } else {
      this.isLoadingImages = false;
      this.originalImageLoaded = false;
      this.watermarkRemovedImageLoaded = false;
      console.log('No selected file, resetting all states');
      this.changeDetectorRef.detectChanges();
    }
  }

  // Check if images are already loaded in the DOM (cached)
  private checkForCachedImages(): void {
    // Check for images in the DOM
    const originalImg = document.querySelector('img[data-image-type="original"]') as HTMLImageElement;
    const watermarkRemovedImg = document.querySelector('img[data-image-type="watermark-removed"]') as HTMLImageElement;

    if (originalImg && originalImg.complete && originalImg.naturalHeight !== 0) {
      console.log('Original image already loaded from cache');
      this.originalImageLoaded = true;
    }

    if (watermarkRemovedImg && watermarkRemovedImg.complete && watermarkRemovedImg.naturalHeight !== 0) {
      console.log('Watermark removed image already loaded from cache');
      this.watermarkRemovedImageLoaded = true;
    }
  }

  // Validation methods
  hasReprocessedImage(): boolean {
    return !!(this.selectedFile?.reprocessed_url);
  }

  getScriptDisplayName(scriptId: string): string {
    const script = this.availableScripts.find(s => s.id === scriptId);
    return script ? script.name : scriptId;
  }

  onValidationChange(validation: 'Correct' | 'Incorrect'): void {
    if (!this.selectedFile) return;

    console.log(`🔄 Updating validation to: ${validation} for file: ${this.selectedFile.page_id}`);
    console.log('🔍 Available scripts before change:', this.availableScripts);
    console.log('📊 Scripts count:', this.availableScripts.length);

    this.bucketService.updateValidation(this.selectedFile.page_id, validation)
      .subscribe({
        next: (response) => {
          console.log('✅ Validation update response:', response);
          if (response.success) {
            // Update local file object
            this.selectedFile!.validation = validation;

            // Clear reprocessed result if marking as correct
            if (validation === 'Correct') {
              this.selectedFile!.reprocessed_url = undefined;
              this.selectedScript = '';
            }

            // If marking as incorrect and no scripts loaded, try loading them
            if (validation === 'Incorrect' && this.availableScripts.length === 0) {
              console.log('🔄 No scripts available, attempting to reload...');
              this.loadAvailableScripts();
            }

            this.showSnackbar(`Validation updated to ${validation}`);
            console.log('📋 Current available scripts after update:', this.availableScripts);
            console.log('📊 Final scripts count:', this.availableScripts.length);
          }
        },
        error: (error) => {
          console.error('❌ Error updating validation:', error);
          console.error('Error details:', {
            message: error.message,
            status: error.status,
            statusText: error.statusText,
            url: error.url
          });
          this.showSnackbar('Error updating validation');
        }
      });
  }

  onReprocessWithScript(): void {
    if (!this.selectedFile || !this.selectedScript) {
      console.warn('⚠️ Cannot reprocess: missing file or script selection');
      console.log('selectedFile:', this.selectedFile?.page_id);
      console.log('selectedScript:', this.selectedScript);
      return;
    }

    console.log(`🔄 Starting reprocessing for file: ${this.selectedFile.page_id} with script: ${this.selectedScript}`);
    this.isReprocessing = true;

    this.bucketService.reprocessWithScript(this.selectedFile.page_id, this.selectedScript)
      .subscribe({
        next: (response) => {
          console.log('✅ Reprocessing response:', response);
          this.isReprocessing = false;

          if (response.success && response.updated_file) {
            // Update the selected file with new data
            this.selectedFile = response.updated_file;
            console.log('📸 Reprocessed URL:', this.selectedFile?.reprocessed_url);
            this.showSnackbar('Alternative processing completed. Review the result below.');
          } else {
            console.warn('⚠️ Reprocessing response indicates failure:', response);
            this.showSnackbar('Alternative processing completed but no result returned.');
          }
        },
        error: (error) => {
          console.error('❌ Error reprocessing image:', error);
          console.error('Error details:', {
            message: error.message,
            status: error.status,
            statusText: error.statusText,
            url: error.url
          });
          this.isReprocessing = false;
          this.showSnackbar('Error during alternative processing');
        }
      });
  }

  onAcceptReprocessedResult(): void {
    if (!this.selectedFile) return;

    this.isAcceptingResult = true;

    this.bucketService.acceptReprocessedResult(this.selectedFile.page_id)
      .subscribe({
        next: (response) => {
          this.isAcceptingResult = false;

          if (response.success && response.updated_file) {
            // Update the selected file - reprocessed result is now the main result
            this.selectedFile = response.updated_file;
            this.selectedScript = '';
            this.showSnackbar('Alternative result accepted successfully!');
          }
        },
        error: (error) => {
          this.isAcceptingResult = false;
          console.error('Error accepting reprocessed result:', error);
          this.showSnackbar('Error accepting result');
        }
      });
  }

  onTryAnotherScript(): void {
    // Clear current reprocessed result and allow selecting another script
    this.selectedScript = '';
    if (this.selectedFile) {
      this.selectedFile.reprocessed_url = undefined;
    }
  }

  private loadAvailableScripts(): void {
    console.log('🔄 Loading available watermark scripts...');
    // Debug API URL from environment
    console.log('🌐 Current environment API URL should be: http://localhost:8080');
    this.bucketService.getWatermarkScripts().subscribe({
      next: (response) => {
        console.log('✅ Scripts API response:', response);
        if (response.success) {
          this.availableScripts = response.scripts;
          console.log('📋 Available scripts loaded:', this.availableScripts);
          console.log('📊 Scripts count:', this.availableScripts.length);
          if (response.note) {
            console.log('ℹ️ Scripts note:', response.note);
          }
        } else {
          console.warn('⚠️ Scripts API returned success=false:', response);
        }
      },
      error: (error) => {
        console.error('❌ Error loading watermark scripts:', error);
        console.error('Error details:', {
          message: error.message,
          status: error.status,
          statusText: error.statusText,
          url: error.url
        });

        // Show user-friendly error
        this.showSnackbar('Failed to load processing options. Please check your connection.');
      }
    });
  }

  private showSnackbar(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  ngAfterViewInit(): void {
    // Debug: track selectedScript changes
    console.log('🔍 Initial selectedScript value:', this.selectedScript);
    
    // Test basic connectivity to Node.js server
    console.log('🧪 Testing basic connectivity to Node.js server...');
    
    // Simple test to see if we can reach any endpoint
    this.bucketService.getWatermarkScripts().subscribe({
      next: (response) => {
        console.log('✅ Server connectivity test passed! Scripts loaded:', response);
      },
      error: (error) => {
        console.error('❌ Server connectivity test failed!', error);
        console.error('🔍 This suggests the Angular app cannot reach the Node.js server');
        console.error('🔍 Check if Node.js server is running on http://localhost:8080');
      }
    });
  }

  onScriptSelectionChange(scriptId: string): void {
    console.log('🔧 Script selection changed to:', scriptId);
    this.selectedScript = scriptId;
    console.log('✅ selectedScript updated to:', this.selectedScript);
    console.log('🔘 Button should be enabled:', !!this.selectedScript && !this.isReprocessing);
  }
}
